import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  model,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  NewRelationsStore,
  NoteActions,
  TASK_FORM_MYSID,
  TASK_MYSID,
  TasksActions,
  UserStore,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { Customer, TaskPayload } from '@qtek/shared/models';
import { FileSizePipe } from '@qtek/shared/pipes';
import { catchError, of, race, filter as rxFilter, take, timer } from 'rxjs';
import {
  CreateNoteActionCardPayload,
  CreateTaskActionCardPayload,
  ExcelDownloadActionCardPayload,
  isCreateNoteActionCardPayload,
  isCreateTaskActionCardPayload,
  isExcelDownloadActionCardPayload,
  isSendEmailActionCardPayload,
  isWordDownloadActionCardPayload,
  SendEmailActionCardPayload,
  WordDownloadActionCardPayload,
} from '../../../models/action-card.types';

/** Remove HTML tags from string (e.g. <p>...</p>) for plain-text display in email body. */
function stripHtml(html: string): string {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<\/?[a-zA-Z][^<>]*>/g, '').trim();
}

/** Turn email local part into a display name (e.g. ana@synply.io -> Ana). */
function emailToDisplayName(email: string): string {
  const local = email.split('@')[0]?.trim() ?? '';
  if (!local) return email;
  return local.charAt(0).toUpperCase() + local.slice(1).toLowerCase();
}

@Component({
  selector: 'qt-ai-chat-action-card',
  standalone: true,
  imports: [
    FormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslationCoreModule,
    FileSizePipe,
  ],
  templateUrl: './ai-chat-action-card.component.html',
  styleUrls: ['./ai-chat-action-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatActionCardComponent {
  @ViewChild('subjectInput') subjectInputRef?: ElementRef<HTMLInputElement>;

  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly newRelationsStore = inject(NewRelationsStore);
  private readonly userStore = inject(UserStore);
  private readonly actions$ = inject(Actions);

  /**
   * We reuse PERSONAL_TASKS mysid since the app already maintains a global
   * subscription to it (from the side panel). Without a matching subscription
   * the WebSocket response has nowhere to go and createTaskSuccess never fires.
   * Correlation is done by task title + author id in the subscribe pipe below.
   */
  private readonly taskMysid = TASK_MYSID.PERSONAL_TASKS;

  readonly payload = input.required<
    | SendEmailActionCardPayload
    | ExcelDownloadActionCardPayload
    | CreateNoteActionCardPayload
    | CreateTaskActionCardPayload
    | unknown
  >();
  /** Previous user message text (e.g. "write email for federico@synply.io") to extract recipient emails when payload has none. */
  readonly userPromptText = input<string>('');

  protected readonly isSendEmail = isSendEmailActionCardPayload;
  protected readonly isExcelDownload = isExcelDownloadActionCardPayload;
  protected readonly isWordDownload = isWordDownloadActionCardPayload;
  protected readonly isCreateNote = isCreateNoteActionCardPayload;
  protected readonly isCreateTask = isCreateTaskActionCardPayload;

  protected readonly excelDownloading = signal(false);
  protected readonly excelError = signal<string | null>(null);

  protected readonly wordDownloading = signal(false);
  protected readonly wordError = signal<string | null>(null);

  // ─── Create Note state ─────────────────────────────────────
  protected readonly noteTitle = model('');
  protected readonly noteContent = model('');
  protected readonly noteError = signal<string | null>(null);
  protected readonly noteCreated = signal(false);

  // ─── Create Task state ─────────────────────────────────────
  protected readonly taskTitle = model('');
  protected readonly taskDescription = model('');
  protected readonly taskDueDate = model('');
  protected readonly taskAssignee = model('');
  /** Selected assignee resolved to a real user (id + name). Null if user typed freely. */
  protected readonly taskAssigneeSelected = signal<{
    id: string;
    name: string;
  } | null>(null);
  protected readonly taskSubmitting = signal(false);
  protected readonly taskError = signal<string | null>(null);
  protected readonly taskCreated = signal(false);

  /** Current authenticated user, used as task author. */
  private readonly currentPerson = this.userStore.currentPerson;

  /** All company contacts from SignalStore, used as suggestions for the assignee autocomplete. */
  private readonly allContacts = this.newRelationsStore.contactsByMysid(
    TASK_FORM_MYSID.ALL_CONTACTS
  );

  /** Build a display name from a Customer (prefers `prs.name`, falls back to email). */
  private contactDisplayName(c: Customer): string {
    return c.prs?.name?.trim() || c.email?.trim() || '';
  }

  /**
   * Filtered contacts matching the current assignee input (case-insensitive substring match).
   *
   * Only contacts with a real `usrId` are surfaced — the id produced here is later
   * written to `TaskPayload.usrId`, which must be a Synply user account id. Falling
   * back to `_id` (Mongo doc id) or `relId` (external contact id) would produce a
   * plausible-looking but invalid payload. This matches `TaskFormComponent.mapUsersToAssigneeOptions`.
   */
  protected readonly assigneeSuggestions = computed<
    Array<{ id: string; name: string }>
  >(() => {
    const query = this.taskAssignee().trim().toLowerCase();
    const contacts = this.allContacts() ?? [];
    const mapped = contacts
      .map(c => ({
        id: c.usrId ?? '',
        name: this.contactDisplayName(c),
      }))
      .filter(c => !!c.id && !!c.name);
    if (!query) return mapped.slice(0, 10);
    return mapped
      .filter(c => c.name.toLowerCase().includes(query))
      .slice(0, 10);
  });

  /** True when the LLM/backend already sent the email (no POST, no button) */
  protected readonly handledByServer = computed(() => {
    const p = this.payload();
    if (!isSendEmailActionCardPayload(p)) return false;
    const m = (p.action?.method ?? '').toLowerCase();
    const pr = (p.action?.protocol ?? '').toLowerCase();
    return (
      m === 'server' ||
      m === 'done' ||
      m === 'llm' ||
      pr === 'server' ||
      pr === 'done' ||
      pr === 'llm'
    );
  });

  protected readonly showMailtoHint = signal(false);

  /** Editable when payload.editable is true */
  protected readonly editable = computed(() => {
    const p = this.payload();
    return isSendEmailActionCardPayload(p) && p.editable !== false;
  });

  /** Recipients from card; empty array if missing */
  protected readonly recipientsList = signal<
    Array<{ name?: string; email: string }>
  >([]);

  /** Single "To" line when card has no recipients array */
  protected readonly toSingle = signal('');

  /** Current value in the "Add recipient..." input when showing chips */
  protected readonly newRecipient = signal('');

  protected readonly subject = signal('');
  protected readonly content = signal('');

  protected readonly hasRecipientsList = computed(
    () => this.recipientsList().length > 0
  );

  protected readonly recipientEmailsLine = computed(() =>
    this.recipientsList()
      .map(r => r.email)
      .filter(Boolean)
      .join(', ')
  );

  constructor() {
    effect(() => {
      this.initializeFromPayload(this.payload());
    });
  }

  private initializeFromPayload(p: unknown): void {
    if (isSendEmailActionCardPayload(p)) {
      this.initializeSendEmailCard(p);
      return;
    }

    if (isCreateNoteActionCardPayload(p)) {
      this.initializeCreateNoteCard(p);
      return;
    }

    if (isCreateTaskActionCardPayload(p)) {
      this.initializeCreateTaskCard(p);
    }
  }

  private initializeSendEmailCard(p: SendEmailActionCardPayload): void {
    const list = this.resolveRecipientsList(p);
    this.recipientsList.set(list.length > 0 ? [...list] : []);
    const sub =
      p.card.subject ??
      (p.card as { emailSubject?: string }).emailSubject ??
      p.card.title ??
      '';
    this.subject.set(String(sub).trim());
    this.content.set(stripHtml(p.card.content ?? ''));
  }

  private resolveRecipientsList(
    p: SendEmailActionCardPayload
  ): Array<{ name?: string; email: string }> {
    const listFromCard = p.card.recipients ?? p.card.receipients ?? [];
    if (listFromCard.length) {
      return listFromCard;
    }

    const listFromPath = this.getRecipientsFromActionPath(p);
    if (listFromPath.length) {
      return listFromPath;
    }

    const listFromCardFields = this.getRecipientsFromCardFields(p);
    if (listFromCardFields.length) {
      return listFromCardFields;
    }

    const listFromActionParams = this.getRecipientsFromActionParams(p);
    if (listFromActionParams.length) {
      return listFromActionParams;
    }

    const listFromPayloadFields = this.getRecipientsFromPayloadFields(p);
    if (listFromPayloadFields.length) {
      return listFromPayloadFields;
    }

    const listFromCardText = this.getRecipientsFromCardText(p);
    if (listFromCardText.length) {
      return listFromCardText;
    }

    return this.getRecipientsFromPromptText();
  }

  private getRecipientsFromActionPath(
    p: SendEmailActionCardPayload
  ): Array<{ name?: string; email: string }> {
    if (!p.action?.params) {
      return [];
    }

    const params = p.action.params as Record<string, unknown>;
    const path =
      (params?.['receipients_path'] as string) ??
      (params?.['recipients_path'] as string);
    if (typeof path !== 'string' || !path.trim()) {
      return [];
    }

    return this.getRecipientsByPath(p, path.trim()) ?? [];
  }

  private getRecipientsFromCardFields(
    p: SendEmailActionCardPayload
  ): Array<{ email: string }> {
    const card = p.card as unknown as Record<string, unknown>;
    const toStr =
      (card?.['to'] as string | undefined) ??
      (card?.['To'] as string | undefined) ??
      (card?.['recipients'] as string | undefined) ??
      (card?.['emails'] as string | undefined);
    if (typeof toStr === 'string' && toStr.trim()) {
      return this.emailsFromDelimitedText(toStr);
    }

    const toArr = Array.isArray(card?.['to'])
      ? (card['to'] as string[])
      : Array.isArray(card?.['recipients'])
        ? (card['recipients'] as string[])
        : [];
    return toArr
      .filter((e): e is string => typeof e === 'string' && !!e.trim())
      .map(email => ({ email }));
  }

  private getRecipientsFromActionParams(
    p: SendEmailActionCardPayload
  ): Array<{ email: string }> {
    if (!p.action?.params) {
      return [];
    }

    const params = p.action.params as Record<string, unknown>;
    const rawTo =
      (params?.['to'] as string | undefined) ??
      (params?.['To'] as string | undefined) ??
      (params?.['recipients'] as string | undefined) ??
      (params?.['emails'] as string | undefined);
    if (typeof rawTo !== 'string' || !rawTo.trim()) {
      return [];
    }

    return this.emailsFromDelimitedText(rawTo);
  }

  private getRecipientsFromPayloadFields(
    p: SendEmailActionCardPayload
  ): Array<{ email: string }> {
    const pl = p as unknown as Record<string, unknown>;
    const rawTo =
      (pl?.['to'] as string | undefined) ??
      (pl?.['recipients'] as string | undefined);
    if (typeof rawTo !== 'string' || !rawTo.trim()) {
      return [];
    }

    return this.emailsFromDelimitedText(rawTo);
  }

  private getRecipientsFromCardText(
    p: SendEmailActionCardPayload
  ): Array<{ email: string }> {
    const text = [p.card.content ?? '', p.card.subject ?? ''].join(' ');
    return this.emailsFromFreeText(text);
  }

  private getRecipientsFromPromptText(): Array<{ email: string }> {
    const prompt = this.userPromptText()?.trim() ?? '';
    if (!prompt) {
      return [];
    }

    return this.emailsFromFreeText(prompt);
  }

  private emailsFromDelimitedText(value: string): Array<{ email: string }> {
    return value
      .split(/[,;\s]+/)
      .map(s => s.trim())
      .filter(Boolean)
      .map(email => ({ email }));
  }

  private emailsFromFreeText(value: string): Array<{ email: string }> {
    const emailRe = /\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+\b/g;
    const emails = [...(value.match(emailRe) ?? [])];
    const unique = [...new Set(emails)];
    return unique.map(email => ({ email }));
  }

  private initializeCreateNoteCard(p: CreateNoteActionCardPayload): void {
    this.noteTitle.set(p.card.title?.trim() ?? '');
    this.noteContent.set(stripHtml(p.card.content ?? ''));
    this.noteError.set(null);
    this.noteCreated.set(false);
  }

  private initializeCreateTaskCard(p: CreateTaskActionCardPayload): void {
    this.taskTitle.set(p.card.title?.trim() ?? '');
    this.taskDescription.set(stripHtml(p.card.description ?? ''));
    this.taskDueDate.set(p.card.dueDate?.trim() ?? '');
    // Leave assignee empty — the user must pick a real contact from the
    // autocomplete so we have a valid usrId. The LLM-suggested name (if any)
    // is only a hint and typically won't match an actual user record.
    this.taskAssignee.set('');
    this.taskAssigneeSelected.set(null);
    this.taskSubmitting.set(false);
    this.taskError.set(null);
    this.taskCreated.set(false);
    // Populate the contacts store so the assignee autocomplete has suggestions.
    this.ensureContactsSubscribed();
  }

  private contactsSubscribed = false;
  private ensureContactsSubscribed(): void {
    if (this.contactsSubscribed) return;
    this.contactsSubscribed = true;
    this.newRelationsStore.connectPeople({
      mysid: TASK_FORM_MYSID.ALL_CONTACTS,
      view: 'prs-basic',
      q: [{ k: 'mysid', op: 'EQ', v: ['task_view_people'] }],
      params: { cmd: 'query', s: 'model' },
    });
  }

  downloadExcel(p: ExcelDownloadActionCardPayload): void {
    this.excelDownloading.set(true);
    this.excelError.set(null);
    const url = p.action?.url ?? `/api/v1/allo/excel/${p.card.docId}`;
    this.http
      .get(url, { responseType: 'blob', withCredentials: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        catchError(() => {
          this.excelError.set('Download failed. Please try again.');
          this.excelDownloading.set(false);
          return of(null);
        }),
        rxFilter((blob: Blob | null): blob is Blob => blob != null)
      )
      .subscribe((blob: Blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = p.card.filename;
        a.click();
        URL.revokeObjectURL(objectUrl);
        this.excelDownloading.set(false);
      });
  }

  downloadWord(p: WordDownloadActionCardPayload): void {
    this.wordDownloading.set(true);
    this.wordError.set(null);
    const url = p.action?.url ?? `/api/v1/allo/word/${p.card.docId}`;
    this.http
      .get(url, { responseType: 'blob', withCredentials: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        catchError(() => {
          this.wordError.set('Download failed. Please try again.');
          this.wordDownloading.set(false);
          return of(null);
        }),
        rxFilter((blob: Blob | null): blob is Blob => blob != null)
      )
      .subscribe((blob: Blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = p.card.filename;
        a.click();
        URL.revokeObjectURL(objectUrl);
        this.wordDownloading.set(false);
      });
  }

  createNote(): void {
    const p = this.payload();
    if (!isCreateNoteActionCardPayload(p)) return;

    const title = this.noteTitle().trim();
    const content = this.noteContent().trim();
    if (!content) {
      this.noteError.set('GNR_NOTE_CONTENT_REQUIRED');
      return;
    }

    this.noteError.set(null);

    // Fire-and-forget dispatch through the WebSocket-backed NgRx action —
    // same optimistic pattern as note-dialog.component.ts. Any open notes
    // subscription (item-dialog notes widget, relation-details, etc.) picks
    // up the `ins` broadcast and refreshes automatically. BE failures surface
    // through the global error toast, as with every other createNote caller.
    // refId must be null (not '') for free-standing notes — DynamoDB's
    // refIdIndx GSI rejects empty strings but accepts null. Matches
    // note-dialog.component.ts:167.
    this.store.dispatch(
      NoteActions.createNote({
        mysid: `allo_note_${Date.now()}`,
        refId: null as unknown as string,
        note: { title, content },
      })
    );
    this.noteCreated.set(true);
  }

  /** True when all required fields are filled and an assignee has been picked. */
  protected readonly canCreateTask = computed(() => {
    return (
      !!this.taskTitle().trim() &&
      !!this.taskAssigneeSelected() &&
      !this.taskSubmitting() &&
      !this.taskCreated()
    );
  });

  createTask(): void {
    const p = this.payload();
    if (!isCreateTaskActionCardPayload(p)) return;

    const title = this.taskTitle().trim();
    if (!title) {
      this.taskError.set('GNR_TASK_TITLE_REQUIRED');
      return;
    }

    const selected = this.taskAssigneeSelected();
    if (!selected) {
      this.taskError.set('GNR_TASK_PICK_ASSIGNEE');
      return;
    }

    const author = this.currentPerson();
    if (!author?.usrId) {
      this.taskError.set('GNR_CURRENT_USER_UNKNOWN');
      return;
    }

    this.taskSubmitting.set(true);
    this.taskError.set(null);

    const payload: TaskPayload = {
      ownerType: 'prs',
      status: 'todo',
      action: 'general',
      params: {
        title: [title],
        desc: [this.taskDescription().trim()],
        assignPrsUsr: [selected.name],
        authorId: [author.usrId],
        authorFullName: [author.name ?? ''],
      },
      executeDate: this.taskDueDate().trim() || this.todayIsoDate(),
      usrId: selected.id,
    };

    // Correlate the response by task title + authorId because we share
    // the global PERSONAL_TASKS mysid (the side panel owns that subscription).
    const matches = (task: {
      usrId?: string;
      params?: Record<string, string[] | undefined>;
    }) =>
      task?.params?.['title']?.[0] === title &&
      task?.params?.['authorId']?.[0] === author.usrId;

    const result$ = race(
      this.actions$.pipe(
        ofType(TasksActions.createTaskSuccess),
        rxFilter(a => matches(a.task as any)),
        take(1)
      ),
      this.actions$.pipe(
        ofType(TasksActions.createTaskFailure),
        rxFilter(a => a.mysid === this.taskMysid),
        take(1)
      ),
      timer(15000)
    );

    result$
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(result => {
        this.taskSubmitting.set(false);
        if (result && typeof result === 'object' && 'type' in result) {
          if (result.type === TasksActions.createTaskSuccess.type) {
            this.taskCreated.set(true);
          } else {
            this.taskError.set('GNR_TASK_CREATION_FAILED');
          }
        } else {
          // timeout
          this.taskError.set('GNR_TASK_CREATION_TIMEOUT');
        }
      });

    this.store.dispatch(
      TasksActions.createTask({ payload, mysid: this.taskMysid })
    );
  }

  /** Returns today's date in yyyy-MM-dd format. */
  private todayIsoDate(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  /** Called when the user picks a name from the assignee autocomplete. */
  onAssigneeOptionSelected(name: string): void {
    const contact = this.assigneeSuggestions().find(c => c.name === name);
    if (contact) {
      this.taskAssigneeSelected.set(contact);
    }
    this.taskAssignee.set(name);
  }

  /** Clears the selected assignee when the user edits the input manually. */
  onAssigneeInput(value: string): void {
    this.taskAssignee.set(value);
    if (this.taskAssigneeSelected()?.name !== value) {
      this.taskAssigneeSelected.set(null);
    }
  }

  setRecipientEmail(index: number, value: string): void {
    this.recipientsList.update(list => {
      const next = [...list];
      if (next[index]) next[index] = { ...next[index], email: value };
      return next;
    });
  }

  removeRecipient(index: number): void {
    this.recipientsList.update(list => list.filter((_, i) => i !== index));
    this.updateGreetingInContent();
  }

  /** When user types "," or ";", add the part before as a recipient chip and keep the rest in the input. */
  onAddRecipientInput(value: string, isListMode: boolean): void {
    const sep = /[,;]/.exec(value);
    if (!sep) {
      if (isListMode) this.newRecipient.set(value);
      else this.toSingle.set(value);
      return;
    }
    const idx = sep.index;
    const first = value.slice(0, idx).trim();
    const rest = value.slice(idx + 1).trim();
    if (first) {
      this.recipientsList.update(list => [...list, { email: first }]);
      this.updateGreetingInContent();
    }
    if (isListMode) {
      this.newRecipient.set(rest);
    } else {
      this.toSingle.set('');
      this.newRecipient.set(rest);
    }
  }

  addRecipientFromInput(event: Event): void {
    event.preventDefault();
    this.commitCurrentRecipientInput();
  }

  /** When leaving the input (blur), if the current value looks like an email, add it as a chip. */
  tryAddRecipientOnBlur(): void {
    this.commitCurrentRecipientInput();
  }

  /** On Tab: create chip from current email if valid, then move focus to Subject. */
  onRecipientInputTab(event: Event): void {
    event.preventDefault();
    this.commitCurrentRecipientInput();
    setTimeout(() => this.subjectInputRef?.nativeElement?.focus(), 0);
  }

  private commitCurrentRecipientInput(): void {
    const value = this.hasRecipientsList()
      ? this.newRecipient().trim()
      : this.toSingle().trim();
    if (!value) return;
    const looksLikeEmail =
      /^[^\s@]{1,64}@[^\s@]{1,253}(?:\.[^\s@]{1,63})+$/.test(value);
    if (!looksLikeEmail) return;
    this.recipientsList.update(list => [...list, { email: value }]);
    this.updateGreetingInContent();
    if (this.hasRecipientsList()) {
      this.newRecipient.set('');
    } else {
      this.toSingle.set('');
      this.newRecipient.set('');
    }
  }

  /**
   * Update the message content greeting (e.g. "Hi Federico,") to include all recipients (e.g. "Hi Federico and Ana,").
   */
  private updateGreetingInContent(): void {
    const list = this.recipientsList();
    if (list.length === 0) return;
    const names = list
      .map(r => r.name?.trim() || emailToDisplayName(r.email))
      .filter(Boolean);
    if (names.length === 0) return;
    const greetingNames = names.join(' and ');
    let body = this.content();
    const hiMatch = new RegExp(/^(\s*)(Hi|Dear|Hello)\s+[^,\n]+,?\s*/i).exec(
      body
    );
    if (hiMatch) {
      const prefix = hiMatch[1] || '';
      const word = hiMatch[2] || 'Hi';
      body =
        prefix +
        word +
        ' ' +
        greetingNames +
        ',' +
        body.slice(hiMatch[0].length);
      this.content.set(body);
    }
  }

  /**
   * Resolve a path like "card.receipients" or "card.receipients.email" from the payload.
   * For "card.receipients.email" we resolve "card.receipients" (array) and pluck .email from each item.
   */
  private getRecipientsByPath(
    payload: SendEmailActionCardPayload,
    path: string
  ): Array<{ name?: string; email: string }> | null {
    const pathToArray = path.endsWith('.email') ? path.slice(0, -6) : path;
    const parts = pathToArray.split('.');
    let cur: unknown = payload as unknown;
    for (const part of parts) {
      if (cur == null || typeof cur !== 'object') return null;
      cur = (cur as Record<string, unknown>)[part];
    }
    if (Array.isArray(cur)) {
      return cur
        .map(item => {
          if (item != null && typeof item === 'object' && 'email' in item) {
            const e = String((item as { email?: string }).email ?? '').trim();
            return e
              ? { email: e, name: (item as { name?: string }).name }
              : null;
          }
          if (typeof item === 'string' && item.trim())
            return { email: item.trim() };
          return null;
        })
        .filter(r => r != null) as Array<{ name?: string; email: string }>;
    }
    return null;
  }

  sendEmail(): void {
    const p = this.payload();
    if (!isSendEmailActionCardPayload(p)) return;

    const emails =
      this.recipientsList().length > 0
        ? this.recipientsList()
            .map(r => r.email?.trim())
            .filter(Boolean)
        : this.toSingle()
            .split(/[,;]/)
            .map(s => s.trim())
            .filter(Boolean);
    if (emails.length === 0) return;

    const sub = this.subject().trim();
    const body = this.content().trim();

    // Open user's mail client via mailto
    const mailto =
      'mailto:' +
      emails.join(',') +
      (sub || body
        ? '?' +
          [
            sub ? `subject=${encodeURIComponent(sub)}` : '',
            body ? `body=${encodeURIComponent(body)}` : '',
          ]
            .filter(Boolean)
            .join('&')
        : '');
    window.location.href = mailto;
    this.showMailtoHint.set(true);
  }
}
